import { type AxiosResponse } from "axios";
import { type SortingDirection } from "../../../../../enums";
import { type WithPagedDataV2 } from "../../../../../interfaces/getPagedDataRequest";
import { type FetchActionPayload } from "../../../../../interfaces/redux";
import { type DistributedOpUpdateParams } from "../../../../../interfaces/updateParams";
import { pluralize } from "../../../../../utils/stringUtils";
import { type AppDispatch, type AppThunk } from "../../../../Application/globaltypes/redux";
import DataService from "../../../../Application/services/dataServices/typedDataService";
import backgroundTask from "../../../../BackgroundTasks/backgroundTask";
import { type SurveyOverview, surveyFilterTypes, type SurveyFiltersEnum } from "../../types/models";
import {
  getSurveysBeginAction,
  getSurveysFailureAction,
  getSurveysSuccessAction,
} from "../actions/surveyOverviewActions";
import { escapeTerm } from "../../../../../utils/searchUtils";
import { formatFilters } from "../../../../../utils/filterMapUtils";
import { type ContentResponse } from "features/Application/services/dataServices/contentDataService";
import { type GenericFiltersMap } from "../../../../../utils/filterUtils";

export const duplicateSurvey =
  (ids: number[], fetchSurveys: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = (surveyIds: number[]) => async () => {
      const result = await DataService.surveys.duplicateSurveyAsync(surveyIds);
      return result;
    };

    const idsCount = ids.length;
    const bannerPrefix = pluralize("Survey", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "DuplicateSurvey",
      title: `Duplication of surveys`,
      onCompleted: () => fetchSurveys(),
      getOperationProps: getOperationProps(ids),
      successTransientMessage: `${bannerPrefix} ${idsCount === 1 ? "has" : "have"} been duplicated!`,
      failureTransientMessage: `${bannerPrefix} duplicate failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const deleteSurvey =
  (ids: number[], fetchSurveys: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = (surveyIds: number[]) => async () => {
      const result = await DataService.surveys.deleteSurveyAsync(surveyIds);
      return result;
    };

    const idsCount = ids.length;
    const bannerPrefix = pluralize("Survey", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "DeleteSurvey",
      title: `Deletion of surveys`,
      onCompleted: () => fetchSurveys(),
      getOperationProps: getOperationProps(ids),
      successTransientMessage: `${bannerPrefix} ${idsCount === 1 ? "has" : "have"} been deleted!`,
      failureTransientMessage: `${bannerPrefix} delete failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export interface GetSurveysParams {
  skip: number;
  top: number;
  sortBy?: string;
  sortOrder?: SortingDirection;
  searchText: string;
  filters: GenericFiltersMap<SurveyFiltersEnum>;
}

const sendGetSurveysRequest = (params: GetSurveysParams): Promise<AxiosResponse<ContentResponse[]>> => {
  const { skip, top, sortBy, sortOrder, searchText, filters: appliedFilters } = params;
  const filters = formatFilters(appliedFilters, surveyFilterTypes);
  const searchTerm = escapeTerm(searchText);

  const request: WithPagedDataV2<any> = {
    top,
    skip,
    sortBy,
    sortOrder,
    term: searchTerm!,
    ...filters,
  };

  return DataService.content.getContent({ ...request, showPurchased: true, contentType: "survey" });
};

export const getSurveys =
  (params: GetSurveysParams): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(getSurveysBeginAction());
    try {
      const result = await sendGetSurveysRequest(params);

      const recordsCount = Number.parseInt(result.headers[countHeaderName]);

      const data: FetchActionPayload<SurveyOverview> = {
        items: result.data,
        totalCount: recordsCount,
      };

      dispatch(getSurveysSuccessAction(data));
    } catch (error: any) {
      dispatch(getSurveysFailureAction(error));
    }
  };

export const changeVisibility =
  (ids: number[], visible: boolean, onSuccess: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const idsCount = ids.length;
    const survey = pluralize("Survey", idsCount);
    const asset = pluralize("Asset", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "VisibilitySurveysDistributedOperation",
      title: `${visible ? "Showing" : "Hiding"} ${asset}`,
      onCompleted: onSuccess,
      getOperationProps: async () => {
        const { data } = await DataService.surveys.changeVisibility(ids, visible);
        return data;
      },
      successTitle: `${survey} ${visible ? "Shown" : "Hidden"}!`,
      successTransientMessage: `${survey} ${idsCount === 1 ? "has" : "have"} been ${visible ? "shown" : "hidden"} successfully!`,
      failureTransientMessage: `${survey} ${visible ? "showing" : "hiding"} failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
